// Full create/edit task form. The client may be "פנימי" (none) — an internal task, which
// links to nothing, so the job field is not offered for it; assignee and due date are
// optional. The note is a plain field here — its content
// is never inspected, only carried to the API as `note`.
import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { tasksApi, clientsApi, jobsApi, authApi, taskListsApi } from '../api';
import he from '../i18n/he';
import { useAlerts } from '../context/AlertsContext';
import Modal from './Modal';
import { TextInput, DateInput, Textarea, Select } from './Form';
import ClientPicker from './ClientPicker';
import DueOffsets from './DueOffsets';
import { URGENCY_ORDER } from './UrgencyBadge';
import { isJobDone } from './StatusBadge';
import { ErrorText } from './Loader';
import TaskListSection from './lists/TaskListSection';
import ListPanel from './lists/ListPanel';
import { definitionOf, toPayload, isComplete } from './lists/shapes';
import { addCopiedRows } from './lists/CopyFromTask';

export default function TaskModal({ task, onClose, onSaved }) {
  // A task can hang off a lead or a customer, so the picker needs every client.
  const { data } = useAsync(() => Promise.all([clientsApi.list('all'), jobsApi.list(), authApi.partners()]));
  const [clients, jobs, partners] = data || [[], [], []];
  const isNew = !task.id;
  const alerts = useAlerts();

  const [form, setForm] = useState({
    title: task.title || '',
    note: task.note || '',
    related_client_id: task.related_client_id || '',
    related_job_id: task.related_job_id || '',
    // Carried through the form untouched, and there is no field for it. It is not
    // editable here — a task's billing engagement is set by whatever opened the task —
    // but it HAS to round-trip: the update sends the whole row, so a value the form
    // never loaded was a value the save wrote back as NULL. Editing a task's due date
    // silently detached it from its retainer. Changing the client clears it, as it
    // clears the job: the retainer was the old client's.
    related_retainer_id: task.related_retainer_id || '',
    due_date: task.due_date || '',
    assigned_partner_id: task.assigned_partner_id || '',
    urgency: task.urgency || 'normal',
  });
  const [error, setError] = useState(null);
  // The task's tracking list (078): a new one being set up here, or the strip of the one it
  // has — refreshed in place when the list is redefined or removed from this dialog.
  const [listDraft, setListDraft] = useState(() => (task.withList ? definitionOf('invitees') : null));
  const [listSummary, setListSummary] = useState(task.list || null);
  const [listTouched, setListTouched] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const refreshList = async () => {
    setListTouched(true);
    try { setListSummary((await taskListsApi.get(task.id)).summary); } catch { setListSummary(null); }
  };
  // Closing after the list was changed here still refreshes the board behind.
  const close = () => (listTouched ? onSaved() : onClose());
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  // Picking a client defaults the assignee to that lead's owner (unless one is
  // already chosen) and resets the job AND the retainer — both belong to the client, so
  // a task moved to another client must not keep pointing at the old one's work. Picking
  // the same client again changes nothing.
  const onClientChange = (id) => {
    const picked = clients.find((c) => String(c.id) === String(id));
    setForm((f) => (String(f.related_client_id) === String(id) ? f : {
      ...f,
      related_client_id: id,
      related_job_id: '',
      related_retainer_id: '',
      assigned_partner_id: f.assigned_partner_id || picked?.owner_partner_id || '',
    }));
  };

  // The client's jobs that are still open — the only forward-looking thing a job's
  // status actually stops. Attaching tomorrow's task to work marked finished is the
  // noise; the finished job itself is untouched and every task already on it stays.
  //
  // The job this task ALREADY points at is always in the list, even once finished.
  // `Select` renders no placeholder option, so dropping the selected value would show
  // the first job while the state still held the old id — displayed and selected
  // disagreeing, and an unrelated edit silently re-pointing the task.
  const jobOptions = [
    { value: '', label: he.board.noJob },
    ...jobs
      .filter((j) => String(j.client_id) === String(form.related_client_id))
      .filter((j) => !isJobDone(j.status) || String(j.id) === String(form.related_job_id))
      .map((j) => ({ value: j.id, label: j.title })),
  ];
  const partnerOptions = [
    { value: '', label: he.board.unassigned },
    ...partners.map((p) => ({ value: p.id, label: p.name })),
  ];

  const internal = !Number(form.related_client_id);

  const submit = async () => {
    try {
      const payload = {
        title: form.title,
        note: form.note,
        due_date: form.due_date || null,
        related_client_id: Number(form.related_client_id) || null,
        // An internal task links to nothing (the server refuses a job or retainer on it);
        // a meeting or an old row may have handed one over, so it is dropped here.
        related_job_id: internal ? null : Number(form.related_job_id) || null,
        related_retainer_id: internal ? null : Number(form.related_retainer_id) || null,
        assigned_partner_id: Number(form.assigned_partner_id) || null,
        urgency: form.urgency,
      };
      const list = listDraft ? toPayload(listDraft) : null;
      let id = task.id;
      if (isNew) id = (await tasksApi.create(list ? { ...payload, list } : payload)).id;
      else {
        await tasksApi.update(task.id, payload);
        if (list) await taskListsApi.create(task.id, list);
      }
      if (list) await addCopiedRows(id, listDraft);
      // A due date is what makes the bell ring, so saving one refreshes it rather than
      // waiting for the next full page load to notice.
      alerts?.reload();
      // A list just made opens straight away, ready for the first name.
      onSaved(list ? { openListFor: id } : undefined);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={isNew ? he.board.newTask : he.tasks.editTask}
      onClose={close}
      onSave={submit}
      saveDisabled={!form.title || (listDraft && !isComplete(listDraft))}
    >
      <ClientPicker label={he.board.client} value={form.related_client_id} onChange={onClientChange} clients={clients} emptyLabel={he.tasks.internal} />
      <TextInput label={he.board.taskTitle} value={form.title} onChange={set('title')} />
      <Textarea label={he.board.note} value={form.note} onChange={set('note')} placeholder={he.board.notePlaceholder} />
      {!internal && (
        <Select label={he.board.job} value={form.related_job_id} onChange={set('related_job_id')} options={jobOptions} />
      )}
      <Select label={he.board.assignee} value={form.assigned_partner_id} onChange={set('assigned_partner_id')} options={partnerOptions} />
      {/* THE SCHEDULING PAIR, and the order is the point: pick "in a week" and read back
          the date it means. The offsets write into the same field the box types into —
          one value, not a mode — so there is nothing to reconcile and no way for the
          buttons and the box to disagree. */}
      <DueOffsets value={form.due_date} onPick={(due) => setForm((f) => ({ ...f, due_date: due }))} />
      <DateInput label={he.tasks.dueDate} value={form.due_date} onChange={set('due_date')} />
      <Select
        label={he.urgencyLabel}
        value={form.urgency}
        onChange={set('urgency')}
        options={URGENCY_ORDER.map((u) => ({ value: u, label: he.taskUrgency[u] }))}
      />
      <TaskListSection
        task={{ ...task, list: listSummary }}
        draft={listDraft}
        onDraft={setListDraft}
        onOpen={() => setPanelOpen(true)}
        onChanged={refreshList}
      />
      {error && <ErrorText error={error} />}
      {panelOpen && <ListPanel taskId={task.id} onClose={() => { setPanelOpen(false); refreshList(); }} />}
    </Modal>
  );
}
